<template>
  <div class="order-or-shipment-template-controls">
    <h1
      id="content-heading"
      class="visually-hidden"
      aria-hidden="true"
      tabindex="-1"
    >
      {{ headerText }}
    </h1>

    <RygenDropdown
      id="order-or-shipment-template-selector"
      v-model="internalSelectedTemplate"
      :options="templateOptions"
      :disabled="orderOrShipmentTemplates.loading || disabled"
      :loading="orderOrShipmentTemplates.loading"
      :placeholder="
        t('templates.findParamTemplate', {
          param: t('common.anParam', { param: t('orders.orders') }),
        })
      "
      :show-clear="!!internalSelectedTemplate"
      class="order-or-shipment-template-selector"
      option-label="name"
      filter
    >
      <template #value="{ value }">
        <HeaderIcon v-if="headerIcon" :icon="headerIcon" flat />
        <span class="header-text">{{ headerText }}</span>
        <span class="slash-separator">/</span>
        <span class="order-or-shipment-template-value">
          {{
            value?.name ??
            t('common.noParam', {
              param: t('templates.templates'),
            })
          }}
        </span>

        <RygenChip
          v-if="isDefaultTemplate(value)"
          :label="t('common.default')"
          :severity="Severity.INFO_LIGHT"
        />
      </template>

      <template #option="{ option }: Option">
        <OrderOrShipmentTemplateOption
          :template="option"
          @remove-as-default="onRemoveDefaultTemplate"
          @save-as-default="onSetDefaultTemplate"
        />
      </template>
    </RygenDropdown>

    <span v-if="edited && !disabled" class="edit-indicator min-w-max">
      - {{ t('common.edited') }}
    </span>

    <div v-if="!disabled" class="save-order-or-shipment-template-container">
      <OrderOrShipmentTemplateActionButton
        :selected-template="internalSelectedTemplate"
        :disabled="orderOrShipmentTemplates.loading"
        :edited="edited"
        :loading="orderOrShipmentTemplates.loading"
        class="align-self-end"
        @command="onOrderOrShipmentTemplateCommand"
      />
      <OrderOrShipmentTemplateModal
        v-model:visible="saveOrderOrShipmentTemplateModal.visible"
        :name="internalSelectedTemplate?.name ?? ''"
        :templates="orderOrShipmentTemplates.data"
        @save="onOrderOrShipmentTemplateConfirm"
      />
    </div>
  </div>
</template>

<script lang="ts" setup>
import isEqual from 'lodash/isEqual';
import type { Ref } from 'vue';
import { computed, ref, toRefs, watch } from 'vue';
import { useI18n } from 'vue-i18n';

import { useSaveConfirmation } from '@rygen/rygen-ui/composables';
import { Severity } from '@rygen/rygen-ui/model';
import { alphabeticComparator } from '@rygen/rygen-ui/util';

import { useOrderOrShipmentTemplates } from '@/composables';
import {
  ManualShipment,
  Order,
  OrderOrShipmentTemplate,
  OrderOrShipmentTemplateAction,
  OrderOrShipmentTemplateActionCommand,
} from '@/model';
import { useParentEntityStore } from '@/stores/ParentEntity';

import OrderOrShipmentTemplateActionButton from '@/components/orderOrShipmentTemplate/OrderOrShipmentTemplateActionButton.vue';
import OrderOrShipmentTemplateModal from '@/components/orderOrShipmentTemplate/OrderOrShipmentTemplateModal.vue';
import OrderOrShipmentTemplateOption from '@/components/orderOrShipmentTemplate/OrderOrShipmentTemplateOption.vue';
import HeaderIcon from '@rygen/rygen-ui/components/content/HeaderIcon.vue';
import RygenDropdown from '@rygen/rygen-ui/components/form/RygenDropdown.vue';
import RygenChip from '@rygen/rygen-ui/components/ui/RygenChip.vue';

type Option = { option: OrderOrShipmentTemplate };

const props = defineProps({
  selectedTemplate: {
    type: OrderOrShipmentTemplate,
    default: () => undefined,
  },
  headerIcon: {
    type: String,
    default: () => '',
  },
  headerText: {
    type: String,
    default: () => '',
  },
  edited: {
    type: Boolean,
    default: () => false,
  },
  disabled: {
    type: Boolean,
    default: () => false,
  },
});
const { selectedTemplate, headerIcon, headerText, edited, disabled } =
  toRefs(props);
const emit = defineEmits<{
  (event: 'discard-changes'): void;
  (event: 'update:selected-template', newVal?: OrderOrShipmentTemplate): void;
}>();

const { t } = useI18n();
const {
  createTemplate,
  defaultOrderTemplate,
  defaultShipmentTemplate,
  deleteTemplate,
  loadOrderTemplates,
  loadShipmentTemplates,
  orderOrShipmentTemplates,
  removeDefaultOrderTemplate,
  removeDefaultShipmentTemplate,
  setDefaultOrderTemplate,
  setDefaultShipmentTemplate,
  updateTemplate,
} = useOrderOrShipmentTemplates();
const { parentEntity } = useParentEntityStore();
const { requestSaveConfirmation } = useSaveConfirmation();

const internalSelectedTemplate: Ref<OrderOrShipmentTemplate | undefined> = ref(
  new OrderOrShipmentTemplate(selectedTemplate?.value),
);
const saveOrderOrShipmentTemplateModal = ref({
  visible: false,
  action: OrderOrShipmentTemplateAction.SAVE,
});

const sortByName = alphabeticComparator({ isCaseSensitive: false });

const templateOptions = computed(() => {
  return orderOrShipmentTemplates.value.data
    ? [...orderOrShipmentTemplates.value.data].sort((a, b) =>
        sortByName(a.name ?? '', b.name ?? ''),
      )
    : [];
});

const isDefaultTemplate = (template: OrderOrShipmentTemplate) => {
  return parentEntity.value instanceof Order
    ? template?.defaultOrderTemplate
    : template?.defaultShipmentTemplate;
};

const showOrderOrShipmentTemplateModal = () => {
  saveOrderOrShipmentTemplateModal.value.visible = true;
};

const onSetDefaultTemplate = async (template: OrderOrShipmentTemplate) => {
  internalSelectedTemplate.value =
    parentEntity.value instanceof Order
      ? await setDefaultOrderTemplate(template)
      : await setDefaultShipmentTemplate(template);
};

const onRemoveDefaultTemplate = async (template: OrderOrShipmentTemplate) => {
  internalSelectedTemplate.value =
    parentEntity.value instanceof Order
      ? await removeDefaultOrderTemplate(template)
      : await removeDefaultShipmentTemplate(template);
};

const onOrderOrShipmentTemplateCommand = async (
  command: OrderOrShipmentTemplateActionCommand,
) => {
  saveOrderOrShipmentTemplateModal.value.action = command.action;

  if (command.action === OrderOrShipmentTemplateAction.RENAME) {
    showOrderOrShipmentTemplateModal();
  } else if (command.action === OrderOrShipmentTemplateAction.SAVE_AS) {
    showOrderOrShipmentTemplateModal();
  } else if (!internalSelectedTemplate.value?.id) {
    return;
  } else if (command.action === OrderOrShipmentTemplateAction.DISCARD_CHANGES) {
    emit('discard-changes');
  } else if (command.action === OrderOrShipmentTemplateAction.SET_DEFAULT) {
    onSetDefaultTemplate(internalSelectedTemplate.value);
  } else if (command.action === OrderOrShipmentTemplateAction.REMOVE_DEFAULT) {
    onRemoveDefaultTemplate(internalSelectedTemplate.value);
  } else if (command.action === OrderOrShipmentTemplateAction.DELETE) {
    await deleteTemplate(internalSelectedTemplate.value.id);
    internalSelectedTemplate.value = undefined;
  } else if (command.action === OrderOrShipmentTemplateAction.SAVE) {
    if (internalSelectedTemplate.value.name) {
      const name = internalSelectedTemplate.value.name;
      requestSaveConfirmation({
        param: t('templates.templates', 1),
        accept: () => onOrderOrShipmentTemplateConfirm(name),
      });
    }
  }
};

const onOrderOrShipmentTemplateConfirm = async (name: string) => {
  if (!(
    parentEntity.value instanceof Order ||
    parentEntity.value instanceof ManualShipment
  )) {
    return;
  }

  const action = saveOrderOrShipmentTemplateModal.value.action;
  const template = new OrderOrShipmentTemplate({
    id:
      action === OrderOrShipmentTemplateAction.SAVE_AS
        ? undefined
        : internalSelectedTemplate.value?.id,
    name,
    defaultOrderTemplate:
      action === OrderOrShipmentTemplateAction.SAVE_AS &&
      internalSelectedTemplate.value?.defaultOrderTemplate,
    defaultShipmentTemplate:
      action === OrderOrShipmentTemplateAction.SAVE_AS &&
      internalSelectedTemplate.value?.defaultShipmentTemplate,
    templateData: parentEntity.value,
  });

  if (action === OrderOrShipmentTemplateAction.RENAME) {
    internalSelectedTemplate.value = await updateTemplate(template);
  } else if (action === OrderOrShipmentTemplateAction.SAVE) {
    internalSelectedTemplate.value = await updateTemplate(template);
  } else if (action === OrderOrShipmentTemplateAction.SAVE_AS) {
    internalSelectedTemplate.value = await createTemplate(template);
  }
};

watch(
  () => selectedTemplate?.value,
  (newVal?: OrderOrShipmentTemplate) => {
    internalSelectedTemplate.value = new OrderOrShipmentTemplate(newVal);
  },
  { deep: true },
);

watch(
  internalSelectedTemplate,
  (newVal?: OrderOrShipmentTemplate) => {
    if (!isEqual(selectedTemplate?.value, newVal)) {
      emit('update:selected-template', new OrderOrShipmentTemplate(newVal));
    }
  },
  { deep: true },
);

watch(
  () => parentEntity.value?.owner?.naturalId,
  async (newVal: string | undefined) => {
    if (newVal) {
      if (parentEntity.value instanceof Order) {
        await loadOrderTemplates();
        internalSelectedTemplate.value = defaultOrderTemplate.value;
      } else {
        await loadShipmentTemplates();
        internalSelectedTemplate.value = defaultShipmentTemplate.value;
      }
    }
  },
  { immediate: true },
);
</script>

<style>
:root {
  --order-or-shipment-template-controls-font-color: var(--p-surface-600);
  --order-or-shipment-template-controls-placeholder-font-color: var(
    --p-surface-500
  );
  --order-or-shipment-template-controls-font-size: 16px;
}

.order-or-shipment-template-controls {
  --p-select-background: var(--p-datatable-header-background);
  --p-select-color: var(--order-or-shipment-template-controls-font-color);
  --p-select-placeholder-color: var(
    --order-or-shipment-template-controls-placeholder-font-color
  );
  --p-select-padding-y: 7px;
  --p-select-padding-x: 7px;

  display: flex;
  gap: var(--gap-1);
  align-items: center;

  .order-or-shipment-template-selector.p-select {
    width: initial;

    .p-select-label {
      display: flex;
      align-items: center;
      gap: var(--sp-1);
      font-size: var(--order-or-shipment-template-controls-font-size);
    }
  }

  .edit-indicator {
    color: var(--order-or-shipment-template-controls-font-color);
    font-size: 14px;
  }

  .save-order-or-shipment-template-container {
    flex: 0 0 auto;
  }
}
</style>
